/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import {Box, Divider, Container, useMultiStyleConfig, Text, Button} from '@chakra-ui/react'
import ChakraLink from '@salesforce/retail-react-app/app/components/link'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import RegisterForm from '../../components/register'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
//import useEinstein from '../../hooks/use-einstein'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import useGigyaCustomer from '../../custom-hooks/use-gigya-customer'
import GenericHtmlPage from '../generic-html-page'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const Registration = () => {
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const navigate = useNavigation()
    const {isRegistered} = useCustomerType()
    const stylesLogin = useMultiStyleConfig('Login')
    const location = useLocation()
    const {customParams} = getConfig()
    const intl = useIntl()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const registrationTitleMessage = defineMessage({
        defaultMessage: 'Register a new account',
        id: 'register_form.message.registration_title',
    })

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(registrationTitleMessage),
        description: intl.formatMessage(registrationTitleMessage),
        url: currentPath,
    }

    const {data: customer} = useCurrentCustomer()

    const {data: gigyaCustomer} = useGigyaCustomer(customer, {
        enabled: !!customer?.c_gigyaId,
    })

    useEffect(() => {
        if (
            customer.isRegistered &&
            gigyaCustomer &&
            Object.keys(gigyaCustomer?.data || {}).length > 0
        ) {
            navigate('/account')
        }
    }, [isRegistered])

    return !hideAccount ? (
        <Box
            data-testid="registration-page"
            data-gtm="registration-page"
            {...stylesLogin.loginContainer}
        >
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Seo title="Registration" description="Customer sign up" />
            <Container {...stylesLogin.loginFormContainer}>
                <Button
                    variant={'icon-button'}
                    as={ChakraLink}
                    to={'/'}
                    {...stylesLogin.backToHomepage}
                >
                    <FormattedMessage
                        defaultMessage="Back to homepage"
                        id="login_form.message.back_to_homepage"
                    />
                </Button>
                <Text {...stylesLogin.myAccountTitle}>
                    <FormattedMessage {...registrationTitleMessage} />
                </Text>
                <Divider {...stylesLogin.divider} />
                <RegisterForm clickSignIn={() => navigate('/login')} />
            </Container>
        </Box>
    ) : (
        <GenericHtmlPage />
    )
}

Registration.getTemplateName = () => 'registration'
export default Registration
